"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ShowMaxParticipantsModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onAdjust: () => void;
  currentParticipants: number;
  showMaxParticipants: number;
  showMinParticipants: number;
  showName?: string;
}

export function ShowMaxParticipantsModal({
  isOpen,
  onCancel,
  onAdjust,
  currentParticipants,
  showMaxParticipants,
  showMinParticipants,
  showName,
}: ShowMaxParticipantsModalProps) {
  const { t } = useTranslation();

  const isBelowMin = currentParticipants < showMinParticipants;
  const limit = isBelowMin ? showMinParticipants : showMaxParticipants;

  const title = isBelowMin
    ? t(
        "booking.shows.participantLimit.belowMinTitle",
        "Group size below show minimum",
      )
    : t(
        "booking.shows.participantLimit.exceedsMaxTitle",
        "Group size exceeds show limit",
      );

  const description = isBelowMin
    ? t(
        "booking.shows.participantLimit.belowMinDescription",
        "You have selected {{current}} participants, but this show requires at least {{min}}. Please adjust your group size to continue.",
        {
          current: currentParticipants,
          min: showMinParticipants,
        },
      )
    : t(
        "booking.shows.participantLimit.exceedsMaxDescription",
        "You have selected {{current}} participants, but this show allows a maximum of {{max}}. Please adjust your group size to continue.",
        {
          current: currentParticipants,
          max: showMaxParticipants,
        },
      );

  const adjustLabel = isBelowMin
    ? t(
        "booking.shows.participantLimit.adjustUp",
        "Adjust to {{count}} participants",
        { count: showMinParticipants },
      )
    : t(
        "booking.shows.participantLimit.adjustDown",
        "Adjust to {{count}} participants",
        { count: showMaxParticipants },
      );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
              <Users className="h-5 w-5 text-orange-600" />
            </div>
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription className="space-y-4 text-base">
          <p className="text-sm text-gray-900">{description}</p>
          {showName && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-sm font-medium text-orange-700">{showName}</p>
              <p className="mt-1 text-xs text-orange-600">
                {t(
                  "booking.shows.participantLimit.allowedRange",
                  "Allowed group size: {{min}}–{{max}} participants",
                  {
                    min: showMinParticipants,
                    max: showMaxParticipants,
                  },
                )}
              </p>
            </div>
          )}
        </DialogDescription>

        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            type="button"
          >
            {t("booking.shows.participantLimit.cancel", "Cancel")}
          </Button>
          <Button
            onClick={onAdjust}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            type="button"
          >
            {adjustLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
